import { useState, useEffect } from "react";
import { Activity, TrendingDown, TrendingUp, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { apiCall } from "../App";

const CATEGORY_COLORS = {
  "Highly Engaged": { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30", bar: "#4CAF50" },
  "Active": { bg: "bg-lime-500/15", text: "text-lime-400", border: "border-lime-500/30", bar: "#8BC34A" },
  "Moderate": { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30", bar: "#FFC107" },
  "At Risk": { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30", bar: "#FF9800" },
  "Disengaged": { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30", bar: "#F44336" },
};

const COMPONENT_LABELS = {
  profile_onboarding: "Profile & Onboarding",
  assessments: "Assessments",
  daily_practice: "Daily Practice",
  harmony_pipeline: "Harmony & Pipeline",
  recency: "Recency",
};

const ScoreBar = ({ score, color }) => (
  <div className="h-2 w-full rounded-full bg-cosmic-light/10 overflow-hidden">
    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: color }} />
  </div>
);

const EngagementScores = ({ token, role = "admin" }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [sortBy, setSortBy] = useState("score_asc"); // score_asc = most at-risk first

  useEffect(() => {
    if (!token) return;
    const endpoint = role === "coach" ? "/subscription/coach/engagement-scores" : "/subscription/admin/engagement-scores";
    apiCall("GET", endpoint, null, token).then(r => {
      if (r.success) setData(r.data);
      setLoading(false);
    });
  }, [token, role]);

  if (loading) return <div className="text-chakra-crown/40 text-sm text-center py-6">Loading engagement data...</div>;
  if (!data || !data.clients) return null;

  const sorted = [...data.clients].sort((a, b) => {
    if (sortBy === "score_asc") return a.score - b.score;
    if (sortBy === "score_desc") return b.score - a.score;
    return a.name.localeCompare(b.name);
  });

  const summary = data.summary || {};
  const catOrder = ["Disengaged", "At Risk", "Moderate", "Active", "Highly Engaged"];

  return (
    <div data-testid="engagement-scores-section">
      {/* Summary Row */}
      <div className="flex items-center gap-4 mb-4 p-3 rounded-xl bg-cosmic-light/5 border border-divine-gold/10" data-testid="engagement-summary">
        <Activity className="w-5 h-5 text-divine-gold/60 shrink-0" />
        <div className="flex items-center gap-4 flex-wrap text-xs">
          <span className="text-chakra-crown/60">
            Avg Score: <span className="font-heading text-divine-gold text-sm">{summary.average_score}</span>/100
          </span>
          {catOrder.map(cat => {
            const count = (summary.categories || {})[cat] || 0;
            if (count === 0) return null;
            const c = CATEGORY_COLORS[cat];
            return (
              <span key={cat} className={`px-2 py-0.5 rounded-full ${c.bg} ${c.text} border ${c.border}`}>
                {count} {cat}
              </span>
            );
          })}
        </div>
      </div>

      {/* Sort Controls */}
      <div className="flex items-center gap-2 mb-3">
        <span className="text-chakra-crown/40 text-xs">Sort:</span>
        {[
          { key: "score_asc", label: "Most At-Risk", icon: TrendingDown },
          { key: "score_desc", label: "Most Engaged", icon: TrendingUp },
          { key: "name", label: "Name", icon: null },
        ].map(s => (
          <button key={s.key} onClick={() => setSortBy(s.key)}
            className={`px-2.5 py-1 rounded-lg text-xs transition-all ${sortBy === s.key ? "bg-divine-gold/20 text-divine-gold border border-divine-gold/30" : "bg-cosmic-light/5 text-chakra-crown/50 border border-transparent hover:border-divine-gold/20"}`}
            data-testid={`sort-${s.key}`}>
            {s.icon && <s.icon className="w-3 h-3 inline mr-1" />}
            {s.label}
          </button>
        ))}
      </div>

      {/* Client List */}
      <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="engagement-client-list">
        {sorted.map(client => {
          const cat = CATEGORY_COLORS[client.label] || CATEGORY_COLORS["Disengaged"];
          const isExpanded = expanded === client.user_id;
          return (
            <div key={client.user_id} className={`rounded-xl border ${cat.border} ${isExpanded ? cat.bg : "bg-cosmic-light/3"} transition-all`}>
              <button onClick={() => setExpanded(isExpanded ? null : client.user_id)}
                className="w-full p-3 flex items-center gap-3 text-left" data-testid={`engagement-client-${client.user_id}`}>
                {/* Score Circle */}
                <div className="relative w-10 h-10 shrink-0">
                  <svg viewBox="0 0 36 36" className="w-10 h-10 -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="#ffffff08" strokeWidth="3" />
                    <circle cx="18" cy="18" r="15" fill="none" stroke={client.color} strokeWidth="3"
                      strokeDasharray={`${client.score * 0.94} 100`} strokeLinecap="round" />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-xs font-heading ${cat.text}`}>
                    {Math.round(client.score)}
                  </span>
                </div>

                {/* Name & Status */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-chakra-crown/90 text-sm font-medium truncate">{client.name || "Unnamed"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${cat.bg} ${cat.text}`}>{client.label}</span>
                  </div>
                  <span className="text-chakra-crown/40 text-xs truncate block">{client.email}</span>
                </div>

                {/* Mini bar */}
                <div className="w-24 hidden sm:block">
                  <ScoreBar score={client.score} color={client.color} />
                </div>

                {client.score < 30 && <AlertTriangle className="w-4 h-4 text-orange-400/60 shrink-0" />}
                {isExpanded ? <ChevronUp className="w-4 h-4 text-chakra-crown/30 shrink-0" /> : <ChevronDown className="w-4 h-4 text-chakra-crown/30 shrink-0" />}
              </button>

              {/* Expanded Component Breakdown */}
              {isExpanded && client.components && (
                <div className="px-3 pb-3 pt-1 border-t border-divine-gold/10" data-testid={`engagement-detail-${client.user_id}`}>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(client.components).map(([key, val]) => (
                      <div key={key} className="text-center">
                        <div className="text-xs text-chakra-crown/40 mb-1">{COMPONENT_LABELS[key] || key}</div>
                        <div className={`font-heading text-sm ${val >= 60 ? "text-emerald-400" : val >= 30 ? "text-amber-400" : "text-red-400"}`}>
                          {Math.round(val)}
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-cosmic-light/10 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{
                            width: `${val}%`,
                            backgroundColor: val >= 60 ? "#4CAF50" : val >= 30 ? "#FFC107" : "#F44336"
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {sorted.length === 0 && (
          <p className="text-chakra-crown/30 text-sm text-center py-8">No clients to display</p>
        )}
      </div>
    </div>
  );
};

export default EngagementScores;
